'use client';

import Link from 'next/link';

import { Button } from '@eliadmin/core/src/components/ui/button';
import { ButtonIcon } from '@eliadmin/core/src/components/ui/button-icon';
import { Heading } from '@eliadmin/core/src/components/ui/heading';
import { Paragraph } from '@eliadmin/core/src/components/ui/paragraph';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { formatSkDateTime } from './date-utils';

export interface ReservationFormSuccessProps {
  submittedAt: Date;
  email: string;
  depositAmount: number;
}

export function ReservationFormSuccess({
  submittedAt,
  email,
  depositAmount,
}: ReservationFormSuccessProps) {
  const { t } = useTranslations();
  const contact = useGlobalStore(s => s.generalLayout.contact);

  return (
    <div
      className={cn(
        'flex w-full flex-col items-center justify-center gap-6 sm:gap-8 lg:gap-12',
        'ea-reservation-form-success',
      )}
    >
      <div className="flex w-full flex-col items-center justify-center gap-3 sm:gap-4 lg:gap-6">
        <ButtonIcon
          alt="Rezervácia bola odoslaná"
          url="/images/icons/check.svg"
          className="max-h-16 min-h-16 min-w-16 max-w-16"
        />
        <Heading center title="Rezervácia bola odoslaná" tag="h3" />
        <Paragraph size="lg" className="w-full text-center">
          Pozrite si potvrdenie v e-maili a uhraďte zálohu do 7 dní.
        </Paragraph>
      </div>
      <div className="flex w-full flex-col items-start justify-start gap-3 rounded-[16px] bg-[#F2EEE7] p-4 sm:flex-row sm:items-center sm:justify-between sm:rounded-[24px] md:p-5 lg:p-6">
        <div className="flex flex-col items-start justify-start gap-1">
          <Paragraph>Číslo rezervácie</Paragraph>
          <Paragraph className="font-bold" size="lg">
            USR-2026-0142
          </Paragraph>
        </div>
        <div className="flex flex-col items-start justify-start gap-1 sm:items-end sm:justify-end">
          <Paragraph className="text-left sm:text-right">{t('text4103')}</Paragraph>
          <Paragraph className="text-left font-bold sm:text-right" size="lg">
            {formatSkDateTime(submittedAt)}
          </Paragraph>
        </div>
      </div>
      <div className="flex w-full flex-col items-start justify-start gap-3 sm:gap-2">
        <Paragraph>Čo bude nasledovať?</Paragraph>
        <div className="flex w-full items-center justify-start gap-3.5 rounded-[16px] border p-4 sm:rounded-[24px] sm:p-5 lg:p-6">
          <div className="flex max-h-[28px] min-h-[28px] min-w-[28px] max-w-[28px] items-center justify-center rounded-full bg-secondary">
            <Heading tag="h6" title="1" center color="white" />
          </div>
          <div className="flex w-full flex-col items-start justify-start gap-1">
            <Paragraph className="font-bold" size="lg">
              Skontrolujte e-mail
            </Paragraph>
            <Paragraph size="sm">
              Do 24 h dostanete potvrdenie s detailmi rezervácie na {email}.
            </Paragraph>
          </div>
        </div>
        <div className="flex w-full items-center justify-start gap-3.5 rounded-[16px] border p-4 sm:rounded-[24px] sm:p-5 lg:p-6">
          <div className="flex max-h-[28px] min-h-[28px] min-w-[28px] max-w-[28px] items-center justify-center rounded-full bg-secondary">
            <Heading tag="h6" title="2" center color="white" />
          </div>
          <div className="flex w-full flex-col items-start justify-start gap-1">
            <Paragraph className="font-bold" size="lg">
              Uhraďte zálohu ({depositAmount} €)
            </Paragraph>
            <Paragraph size="sm">Platobné údaje nájdete v e-maili. Splatnosť do 7 dní.</Paragraph>
          </div>
        </div>
        <div className="flex w-full items-center justify-start gap-3.5 rounded-[16px] border p-4 sm:rounded-[24px] sm:p-5 lg:p-6">
          <div className="flex max-h-[28px] min-h-[28px] min-w-[28px] max-w-[28px] items-center justify-center rounded-full bg-secondary">
            <Heading tag="h6" title="3" center color="white" />
          </div>
          <div className="flex w-full flex-col items-start justify-start gap-1">
            <Paragraph className="font-bold" size="lg">
              Pred príchodom
            </Paragraph>
            <Paragraph size="sm">
              3 dni pred príchodom Vám pošleme adresu, kde si vyzdvihnete kľúče a praktické info.
            </Paragraph>
          </div>
        </div>
      </div>
      <Button asChild variant={'outline'}>
        <Link href={ROUTES.HOME()}>Domov</Link>
      </Button>
      {(contact?.phone || contact?.email) && (
        <Paragraph>
          Otázka?{' '}
          {contact?.phone && (
            <>
              Zavolajte{' '}
              <Link href={`tel:${contact.phone}`} target="_blank" className="font-bold">
                {contact.phone}
              </Link>{' '}
            </>
          )}
          {contact?.phone && contact?.email && 'alebo napíšte '}
          {contact?.email && (
            <Link href={`mailto:${contact.email}`} className="font-bold">
              {contact.email}
            </Link>
          )}
        </Paragraph>
      )}
    </div>
  );
}
